// src/features/event/pages/EventRegisterPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { customConfirm } from "@/ui/confirm";
import { COLOR, FW, RADIUS } from "@/ui/tokens";
import { eventCard, EVENT_LOGO, withBase } from "../eventCard";

const ICONS = {
    showPw: withBase("icons/show_pw.svg"),
    hidePw: withBase("icons/hide_pw.svg"),
};

const AUTH_BASE = (import.meta.env.VITE_AUTH_BASE ?? import.meta.env.VITE_AUTH_BASE ?? "").replace(/\/+$/, "");

type Agreements = {
    terms_version: string;
    privacy_version: string;
    marketing_sms?: boolean;
};

type ServerError = { error?: string; message?: string; fields?: string[] };

export default function EventRegisterPage() {
    const nav = useNavigate();
    const loc = useLocation() as any;

    const [ready, setReady] = useState(false);
    const [agreements, setAgreements] = useState<Agreements | null>(null);

    const [username, setUsername] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [showPw2, setShowPw2] = useState(false);

    const [usernameChecked, setUsernameChecked] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
    const [usernameHint, setUsernameHint] =
        useState<{ type: "ok" | "err"; text: string } | null>(null);

    const [phoneSessionId, setPhoneSessionId] = useState<string | null>(null);
    const [phoneProof, setPhoneProof] = useState<string | null>(null);

    const phoneProofRef = useRef<string | null>(null);
    useEffect(() => { phoneProofRef.current = phoneProof; }, [phoneProof]);

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [msg, setMsg] = useState<string | null>(null);

    const phoneSessionRef = useRef<string | null>(null);
    const confirmInFlightRef = useRef(false);
    const confirmedRef = useRef(false);

    useEffect(() => { confirmedRef.current = !!phoneProof; }, [phoneProof]);
    useEffect(() => { phoneSessionRef.current = phoneSessionId; }, [phoneSessionId]);

    useEffect(() => {
        let ag: Agreements | null = null;
        const st: Agreements | undefined = loc.state?.agreements;
        if (st) ag = st;
        else {
            try {
                ag = JSON.parse(sessionStorage.getItem("bt.agreements") || "null");
            } catch {}
        }
        if (!ag?.terms_version || !ag?.privacy_version) {
            nav("/event/agree", { replace: true });
            return;
        }
        setAgreements(ag);
        setReady(true);
    }, [loc.state, nav]);

    const usernameOk = useMemo(() => /^[A-Za-z0-9]{1,10}$/.test(username.trim()), [username]);
    const pwPolicyOk = useMemo(() => /^(?=.*[A-Za-z])(?=.*\d).{4,20}$/.test(pw), [pw]);
    const pwMatchOk = useMemo(() => pw2 === pw && pw.length > 0, [pw, pw2]);
    const pw2Touched = useMemo(() => pw2.length > 0, [pw2]);

    const pwMatchText = useMemo(() => {
        if (!pw2Touched) return null;
        if (pw2 !== pw) return { type: "err", text: "비밀번호가 일치하지 않습니다." };
        if (!pwPolicyOk) return null; // match but policy not met — suppress ok message
        return { type: "ok", text: "비밀번호가 일치합니다." };
    }, [pw, pw2, pw2Touched, pwPolicyOk]);

    const canStartVerification =
        usernameOk && pwPolicyOk && pwMatchOk && usernameChecked && usernameAvailable === true && !phoneProof;

    const canRegister =
        usernameOk && pwPolicyOk && pwMatchOk && usernameChecked && usernameAvailable === true && !!phoneProof;

    const [pwPolicyTried, setPwPolicyTried] = useState(false);
    const showPwPolicyWarning = pwPolicyTried && !pwPolicyOk;
    const showDupCheckReminder = pw.length > 0 && !usernameChecked && !usernameHint;

    useEffect(() => {
        setUsernameChecked(false);
        setUsernameAvailable(null);
        setUsernameHint(null);
    }, [username]);

    useEffect(() => {
        const onMessage = (ev: MessageEvent) => {
            const d: any = ev.data;
            if (!d || d.type !== "KMC_DONE") return;
            const sid = String(d.session_id || "");
            const cur = phoneSessionRef.current;
            if (sid && cur && sid !== cur) return;
            void confirmPhoneVerification();
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showError = (text: string) => { setErr(text); setMsg(null); };
    const showMessage = (text: string) => { setMsg(text); setErr(null); };

    const checkUsername = async () => {
        setErr(null); setMsg(null); setUsernameHint(null);
        const u = username.trim();
        if (!u) return;
        if (!usernameOk) {
            setUsernameHint({ type: "err", text: "아이디는 영문/숫자만, 최대 10자입니다." });
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${AUTH_BASE}/username/available?username=${encodeURIComponent(u)}`);
            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const ok = !!data?.available;
            setUsernameChecked(true);
            setUsernameAvailable(ok);
            if (ok) setUsernameHint({ type: "ok", text: "사용 가능한 아이디입니다." });
            else setUsernameHint({ type: "err", text: "이미 사용 중인 아이디입니다." });
        } catch {
            setUsernameHint({ type: "err", text: "아이디 중복확인 실패" });
        } finally {
            setBusy(false);
        }
    };

    const startPhoneVerification = async () => {
        setErr(null); setMsg(null); setPhoneProof(null);
        if (!canStartVerification) {
            if (!usernameOk) return showError("아이디 형식을 확인해주세요.");
            if (!pwPolicyOk) return showError("비밀번호는 4~20자, 영문+숫자를 포함해야 합니다.");
            if (!pwMatchOk) return showError("비밀번호가 일치하지 않습니다.");
            if (!usernameChecked) return showError("아이디 중복확인을 먼저 진행해주세요.");
            if (usernameAvailable === false) return showError("이미 사용 중인 아이디입니다.");
            return;
        }
        const popup = window.open(
            "", "KMCISWindow",
            "width=420,height=720,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes"
        );
        if (!popup) {
            showError("팝업이 차단되었습니다. 브라우저에서 팝업 허용 후 다시 시도해주세요.");
            return;
        }
        popup.document.write("<p style='font-family:sans-serif;padding:16px'>본인인증 준비 중...</p>");
        setBusy(true);
        try {
            const r = await fetch(`${AUTH_BASE}/phone/start`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ return_url: "" }),
            });
            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
            const sessionId: string = data.session_id;
            const kmc = data.kmc || {};
            setPhoneSessionId(sessionId);
            const form = popup.document.createElement("form");
            form.method = "POST"; form.action = kmc.action; form.target = "KMCISWindow";
            const add = (k: string, v: string) => {
                const input = popup.document.createElement("input");
                input.type = "hidden"; input.name = k; input.value = v ?? "";
                form.appendChild(input);
            };
            add("tr_cert", kmc.tr_cert || "");
            add("tr_url", kmc.tr_url || "");
            add("tr_ver", kmc.tr_ver || "V2");
            popup.document.body.appendChild(form);
            form.submit();
            showMessage("휴대폰 인증을 완료해주세요.");
            const startedAt = Date.now();
            const timer = window.setInterval(() => {
                if (popup.closed) {
                    window.clearInterval(timer);
                    if (!phoneSessionRef.current) return;
                    if (!phoneProofRef.current) showError("인증이 취소되었거나 중단되었습니다. 다시 시도해주세요.");
                }
                if (Date.now() - startedAt > 10 * 60 * 1000 && !phoneProofRef.current) {
                    window.clearInterval(timer);
                    showError("인증 시간이 만료되었습니다. 다시 시도해주세요.");
                }
            }, 600);
        } catch (e: any) {
            try { popup.close(); } catch {}
            showError(e?.message || "휴대폰 인증 시작 실패");
        } finally {
            setBusy(false);
        }
    };

    const confirmPhoneVerification = async () => {
        const sid = phoneSessionRef.current;
        if (!sid) return;
        if (confirmedRef.current) return;
        if (confirmInFlightRef.current) return;
        confirmInFlightRef.current = true;
        setBusy(true); setErr(null); setMsg(null);
        try {
            const r = await fetch(`${AUTH_BASE}/phone/confirm`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ session_id: sid }),
            });
            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) {
                const code = String((data as ServerError)?.error || "");
                if (code === "SESSION_EXPIRED") { showError("인증 시간이 만료되었습니다. 다시 시도해주세요."); return; }
                showError("인증 확인에 실패했습니다. 다시 시도해주세요.");
                return;
            }
            setPhoneProof(data.phone_proof || null);
            showMessage("휴대폰 인증이 완료되었습니다. 아래 '회원가입 완료'를 눌러 가입을 마무리해주세요.");
        } catch {
            showError("인증 확인에 실패했습니다. 다시 시도해주세요.");
        } finally {
            confirmInFlightRef.current = false;
            setBusy(false);
        }
    };

    const submitRegister = async () => {
        if (!agreements) return;
        if (!canRegister) return;
        setBusy(true); setErr(null); setMsg(null);
        try {
            const r = await fetch(`${AUTH_BASE}/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({
                    username: username.trim(),
                    password: pw,
                    phone_proof: phoneProof,
                    agreements,
                }),
            });
            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) {
                const code = String((data as ServerError)?.error || "");
                if (code === "PHONE_TAKEN" || code === "DI_TAKEN") { showError("이미 가입된 휴대폰 번호입니다."); return; }
                if (code === "USERNAME_TAKEN") { showError("이미 사용 중인 아이디입니다."); setUsernameAvailable(false); setUsernameChecked(true); return; }
                if (code === "PHONE_VERIFICATION_REQUIRED") { showError("휴대폰 인증이 필요합니다."); setPhoneProof(null); return; }
                showError(data?.message || "회원가입에 실패했습니다.");
                return;
            }
            sessionStorage.removeItem("bt.agreements");
            await customConfirm("회원가입이 완료되었습니다. InnoSpaceOne로 체험 해보세요.", "확인");
            window.close();
        } catch {
            showError("회원가입에 실패했습니다.");
        } finally {
            setBusy(false);
        }
    };

    const onPrimaryClick = async () => {
        if (!phoneProof) { await startPhoneVerification(); return; }
        await submitRegister();
    };

    if (!ready || !agreements) return null;

    const primaryLabel = phoneProof ? "회원가입 완료" : "인증하기";
    const primaryDisabled = busy || (phoneProof ? !canRegister : !canStartVerification);

    return (
        <div style={eventCard.wrap}>
            <div style={eventCard.card}>
                {/* Header */}
                <div style={eventCard.header}>
                    <div style={eventCard.logoBox}>
                        <img src={EVENT_LOGO} alt="" style={{ width: 28, height: 28, filter: "invert(1)" }} />
                    </div>
                    <h1 style={eventCard.title}>회원가입</h1>
                    <p style={eventCard.subtitle}>이벤트 참여를 위해 계정을 만들어주세요</p>
                </div>

                {/* Body */}
                <div style={eventCard.body}>
                    {/* 아이디 */}
                    <div style={s.fieldLabel}>아이디</div>
                    <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                        <input
                            placeholder="영문/숫자, 최대 10자"
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            style={{ ...s.input, flex: 1 }}
                        />
                        <button
                            type="button"
                            onClick={checkUsername}
                            disabled={busy || !username.trim()}
                            style={{ ...s.actionBtn, opacity: busy || !username.trim() ? 0.5 : 1 }}
                        >
                            중복확인
                        </button>
                    </div>
                    {usernameHint && (
                        <div style={{ fontSize: 12, fontWeight: FW.bold, color: usernameHint.type === "err" ? COLOR.error : COLOR.success, marginBottom: 8 }}>
                            {usernameHint.text}
                        </div>
                    )}
                    {showDupCheckReminder && (
                        <div style={{ fontSize: 12, fontWeight: FW.bold, color: COLOR.warning, marginBottom: 8 }}>
                            아이디 중복확인을 먼저 진행해주세요.
                        </div>
                    )}

                    {/* 비밀번호 */}
                    <div style={{ ...s.fieldLabel, marginTop: 12 }}>비밀번호</div>
                    <div style={{ position: "relative", marginBottom: 4 }}>
                        <input
                            placeholder="비밀번호 입력"
                            type={showPw ? "text" : "password"}
                            autoComplete="new-password"
                            value={pw}
                            onChange={e => setPw(e.target.value)}
                            onBlur={() => { if (pw.length > 0 && !pwPolicyOk) setPwPolicyTried(true); }}
                            style={{ ...s.input, width: "100%", paddingRight: 40, boxSizing: "border-box" }}
                        />
                        <button type="button" tabIndex={-1} onClick={() => setShowPw(v => !v)} style={s.eyeBtn}>
                            <img src={showPw ? ICONS.hidePw : ICONS.showPw} alt="" style={{ width: 18, height: 18 }} />
                        </button>
                    </div>
                    <div style={{ fontSize: 11, color: showPwPolicyWarning ? COLOR.error : COLOR.textMuted, marginBottom: showPwPolicyWarning ? 2 : 8 }}>
                        4~20자 / 영문+숫자 포함 / 특수문자 사용 가능
                    </div>
                    {showPwPolicyWarning && (
                        <div style={{ fontSize: 12, fontWeight: FW.bold, color: COLOR.error, marginBottom: 8 }}>
                            영문자와 숫자를 각각 하나 이상 입력해주세요.
                        </div>
                    )}

                    {/* 비밀번호 확인 */}
                    <div style={s.fieldLabel}>비밀번호 확인</div>
                    <div style={{ position: "relative", marginBottom: pwMatchText ? 4 : 12 }}>
                        <input
                            placeholder="비밀번호 확인"
                            type={showPw2 ? "text" : "password"}
                            autoComplete="new-password"
                            value={pw2}
                            onChange={e => setPw2(e.target.value)}
                            style={{ ...s.input, width: "100%", paddingRight: 40, boxSizing: "border-box" }}
                        />
                        <button type="button" tabIndex={-1} onClick={() => setShowPw2(v => !v)} style={s.eyeBtn}>
                            <img src={showPw2 ? ICONS.hidePw : ICONS.showPw} alt="" style={{ width: 18, height: 18 }} />
                        </button>
                    </div>
                    {pwMatchText && (
                        <div style={{ fontSize: 12, fontWeight: FW.bold, color: pwMatchText.type === "err" ? COLOR.error : COLOR.success, marginBottom: 12 }}>
                            {pwMatchText.text}
                        </div>
                    )}

                    {/* 상태 메시지 */}
                    {(err || msg) && (
                        <div role="status" style={{ fontSize: 12, color: err ? COLOR.error : COLOR.success, marginBottom: 8 }}>
                            {err || msg}
                        </div>
                    )}

                    {/* 자동 confirm 실패 안전장치 */}
                    {!phoneProof && phoneSessionId && (
                        <button
                            type="button"
                            onClick={confirmPhoneVerification}
                            disabled={busy}
                            style={{ ...s.textBtn, marginBottom: 8 }}
                        >
                            인증 완료 확인
                        </button>
                    )}

                    {/* 주 버튼 */}
                    <button
                        type="button"
                        onClick={onPrimaryClick}
                        disabled={primaryDisabled}
                        style={{ ...eventCard.ctaBtn, opacity: primaryDisabled ? 0.45 : 1, marginTop: 8 }}
                    >
                        {busy ? "처리 중…" : primaryLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

const s: Record<string, React.CSSProperties> = {
    fieldLabel: { fontSize: 13, fontWeight: FW.bold, color: COLOR.textDark, marginBottom: 6 },
    input:      { height: 40, borderRadius: RADIUS.sm, padding: "0 12px", border: "1.5px solid #e4e4e7", fontSize: 14, fontWeight: FW.medium, outline: "none" },
    actionBtn:  { flexShrink: 0, height: 40, padding: "0 14px", borderRadius: RADIUS.sm, background: COLOR.textDark, color: COLOR.white, fontSize: 13, fontWeight: FW.bold, border: "none", cursor: "pointer", whiteSpace: "nowrap" },
    eyeBtn:     { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", cursor: "pointer", padding: 0, lineHeight: 0 },
    textBtn:    { background: "transparent", border: "none", textDecoration: "underline", fontSize: 13, fontWeight: FW.bold, color: COLOR.text, padding: 0, cursor: "pointer" },
};